import { Component, Input, OnChanges, SimpleChanges, OnDestroy } from "@angular/core";
import {animate, state, style, transition, trigger} from '@angular/animations';
import { first } from 'rxjs/operators';
import { StreamService } from "../services/stream.service";
import { ItemService } from "../services/item.service";
import { BehaviorSubject } from "rxjs";
import * as _ from 'lodash';

@Component({
    selector: 'topic-builder-preview',
    animations: [
        trigger('detailExpand', [
          state('collapsed', style({height: '0px', minHeight: '0', display: 'none'})),
          state('expanded', style({height: '*'})),
          transition('expanded <=> collapsed', animate('500ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    styles:[`
        td.mat-column-title {
            width: calc(100vw - 240px)
        }

        tr.details {
            border-top-width: 0px;
            height: 0px;
        }

        tr.item-summary, tr.item-summary td {
            border-bottom-width: 0;
        }

        .detail-description {
            padding-top: 5px;
            padding-bottom: 5px;
            white-space: initial;
        }
    `],
    template: `
        <div *ngIf="noTouching" fxLayout="column" fxLayoutAlign="center center" style="height:100%">
            <spinner syle="padding-top: 100px;"></spinner>
        </div>
        <div *ngIf="!noTouching" 
             class="scroll-container" 
             infiniteScroll
             [infiniteScrollThrottle]="300"
             [scrollWindow]="false"
             (scrolled)="nextPage()">
             <table mat-table *ngIf="!noTouching && items" #table multiTemplateDataRows [dataSource]="items">
                <ng-container matColumnDef="title">
                    <th mat-header-cell *matHeaderCellDef>Title</th>
                    <td mat-cell *matCellDef="let row">
                        <div style="overflow: hidden; white-space: nowrap;" fxFlex>
                            <div style="text-overflow: ellipsis; overflow: hidden;" [matTooltip]="row.title" fxFlex>
                                {{row.title}}
                            </div>
                        </div>
                    </td>
                </ng-container>
                <ng-container matColumnDef="date">
                    <th mat-header-cell *matHeaderCellDef>Created</th>
                    <td mat-cell *matCellDef="let row">
                        {{row.publishedDate | amTimeAgo }}
                    </td>
                </ng-container>
                <ng-container matColumnDef="toggle">
                    <td mat-cell *matCellDef="let row" style="width: 30px; cursor: pointer;">
                        <mat-icon *ngIf="row === expandedItem">keyboard_arrow_down</mat-icon>
                        <mat-icon *ngIf="row !== expandedItem">keyboard_arrow_right</mat-icon>
                    </td>
                </ng-container>
                <ng-container matColumnDef="details">
                    <th mat-header-cell *matHeaderCellDef>Details</th>
                    <td mat-cell *matCellDef="let row" colspan="3" width="100%">
                        <div style="overflow: hidden; white-space: nowrap;">
                            <div style="text-overflow: ellipsis; overflow: hidden;" [matTooltip]="row.title">
                                <div [@detailExpand]="row === expandedItem ? 'expanded' : 'collapsed'">
                                    <div *ngIf="row === expandedItem" 
                                         [innerHtml]="row.defaultDescription" 
                                         class="detail-description main-contents"></div>
                                </div>
                            </div>
                        </div>
                    </td>
                </ng-container>
                
                <tr mat-header-row *matHeaderRowDef="[]"></tr>
                <tr mat-row *matRowDef="let row; columns: ['toggle', 'title', 'date'];"
                    class="item-summary"
                    (click)="expandedItem = expandedItem === row ? null : row"></tr>
                <tr mat-row *matRowDef="let row; columns: ['details'];" class="details"></tr>
            </table>
            <div *ngIf="noTouchingPageFetch" fxLayout="column" fxLayoutAlign="center center">
                <spinner></spinner>
            </div>
        </div>
    `
})
export class TopicBuilderPreviewComponent implements OnChanges, OnDestroy {
    @Input() itemCount;
    @Input() form;
    expandedItem: any;
    items = new BehaviorSubject<any[]>([]);
    searchParams = { 
        sortField: 'publishedDate',
        includeFullDescription: true, 
        includeMarketplaceStreams: true, 
        page: 0, 
        rows: 200, 
        includeReadItems: true 
    };
    allFetched: boolean;
    noTouching: boolean;
    noTouchingPageFetch: boolean;
    filterVal: any;

    constructor(private itemService: ItemService, private streamService: StreamService) {}

    doSearch(page): void {
        if (!this.allFetched) {
            const settings = {search: '', searchConfiguration: {}};
            const search = this.streamService.basicFilterTermFromFilter(this.filterVal);
            settings.search = search?.trim().length ? search : undefined;
            if (this.filterVal && this.filterVal.basicFilterStructures) {
                settings.searchConfiguration = {filterStructures: this.filterVal.basicFilterStructures};
            }
            this.searchParams.page = page;
            this.itemService.searchItemsWithSettings(settings, this.searchParams)
            .pipe(first())
            .subscribe(results => {
                this.noTouching = false;
                this.noTouchingPageFetch = false;
                this.allFetched = results.last;
                this.items.next(this.items.value.concat(results.content));
            });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.itemCount.currentValue) {
            if (!_.eq(this.filterVal, this.form.value)) {
                this.filterVal = this.form.value;
                this.noTouching = true;
                this.items.next([]);
                this.allFetched = false;
                this.doSearch(0);
            }
        }
    }

    nextPage(): void {
        this.noTouchingPageFetch = true;
        this.doSearch(this.searchParams.page++);
    }

    ngOnDestroy(): void {
        this.items.complete();
    }
}